<template>
  <div
    ref="trackEl"
    class="slide-to-confirm"
    :class="[
      `slide-to-confirm--${variant}`,
      {
        'slide-to-confirm--disabled': disabled,
        'slide-to-confirm--loading': loading,
        'slide-to-confirm--armed': armed,
        'slide-to-confirm--dragging': dragging,
      },
    ]"
    role="button"
    :tabindex="disabled || loading ? -1 : 0"
    :aria-disabled="disabled || loading"
    :aria-label="ariaLabel"
    @keydown.enter.prevent="handleKeyConfirm"
    @keydown.space.prevent="handleKeyConfirm"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
    @click="handleClick"
  >
    <div class="slide-to-confirm__fill" :style="fillStyle" aria-hidden="true" />
    <div class="slide-to-confirm__label" :style="labelStyle">
      <slot />
    </div>
    <transition name="stc-fade">
      <div v-if="armed && dragging" class="slide-to-confirm__armed-label">
        {{ armedText }}
      </div>
    </transition>
    <div class="slide-to-confirm__thumb" :style="thumbStyle">
      <span
        v-if="loading"
        class="slide-to-confirm__spinner"
        aria-hidden="true"
      />
      <svg
        v-else
        class="slide-to-confirm__chevrons"
        viewBox="0 0 20 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          class="slide-to-confirm__chevron-1"
          d="M3 2l6 6-6 6"
          stroke="currentColor"
          stroke-width="2.25"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          class="slide-to-confirm__chevron-2"
          d="M10 2l6 6-6 6"
          stroke="currentColor"
          stroke-width="2.25"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  // Greys out the control and ignores all input.
  disabled: { type: Boolean, default: false },
  // While true the thumb stays pinned at the end with a spinner. On the
  // true -> false transition the thumb springs back so the control is
  // re-usable if the parent didn't close (rejection, slippage modal, etc.).
  loading: { type: Boolean, default: false },
  // 'primary' matches .take-bet-btn, 'offer' matches the lighter
  // .create-offer-btn background, 'max' matches the orange max-balance path.
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'offer', 'max'].includes(v),
  },
  armedText: { type: String, default: 'Release to confirm' },
  ariaLabel: { type: String, default: 'Slide to confirm' },
});

// 'abandon' fires once per gesture as soon as the thumb travels
// ATTEMPT_MIN_PX (see markAttempt for why it's emitted at the crossing, not
// at gesture end). Parents hold it as a pending "abandoned" flag until the
// modal closes; 'confirm' cancels it.
const emit = defineEmits(['confirm', 'abandon']);

// Fraction of the track the thumb must travel before release confirms.
// Effectively the full track (Kalshi-style) — the small tolerance only
// keeps the last few px of travel from being a precision test.
const CONFIRM_THRESHOLD = 0.95;
// How far outside the thumb a press may land and still start the drag.
// Fingers are imprecise — a strict thumb-only target made the control feel
// hard to grab. Anything further right along the track is a tap, not a
// grab, and must never start a drag (a tap at the far end would otherwise
// arm and confirm).
const GRAB_SLOP_PX = 28;
// Haptic cutoff: a springback past this fraction gets the "nothing was
// placed" tick. Haptics only — attempt detection for analytics is
// ATTEMPT_MIN_PX below.
const ATTEMPT_THRESHOLD = 0.25;
// A gesture counts as a slide attempt once the thumb has traveled this many
// px. Absolute px, not a track fraction: the question is "did the user
// engage the control at all", and a track-fraction gate (the old 0.25)
// silently dropped the majority of real give-ups — short pulls released
// early. Small enough to catch those, large enough that a press consumed by
// a vertical scroll (pan-y steals the pointer with near-zero horizontal
// travel) stays silent.
const ATTEMPT_MIN_PX = 10;
// If the parent's confirm handler bails out synchronously without ever
// setting loading (e.g. the slippage-confirm modal path in takeBet), the
// thumb would sit pinned forever. Spring back if loading hasn't started
// within this window.
const LOADING_GRACE_MS = 450;

const trackEl = ref(null);
const x = ref(0);
const dragging = ref(false);
const settled = ref(false); // confirm emitted, ignoring input until reset
const trackWidth = ref(0);
const thumbWidth = ref(0);

let pointerId = null;
let grabOffset = 0;
let trackLeft = 0;
let maxX = 1;
let graceTimer = null;
let lastArmed = false;
let pendingClientX = null;
let moveRaf = 0;
// Whether the current gesture has crossed ATTEMPT_MIN_PX (one 'abandon' emit
// per gesture, at the crossing).
let attempted = false;

const progress = computed(() => (maxX > 0 ? x.value / maxX : 0));
const armed = computed(() => progress.value >= CONFIRM_THRESHOLD);

const thumbStyle = computed(() => ({
  transform: `translateX(${x.value}px)${dragging.value ? ' scale(1.05)' : ''}`,
}));

// Composited fill: a full-width layer scaled from the left edge. Animating
// transform instead of width keeps the drag on the GPU — width changes
// force layout every frame and visibly stutter on phones.
const fillStyle = computed(() => {
  const scale =
    trackWidth.value > 0
      ? (x.value + thumbWidth.value + TRACK_INSET) / trackWidth.value
      : 0.18;
  return { transform: `scaleX(${Math.min(scale, 1)})` };
});

const labelStyle = computed(() => ({
  opacity: String(Math.max(0, 1 - progress.value * 1.6)),
}));

function measureTrack() {
  const track = trackEl.value;
  if (!track) return;
  const rect = track.getBoundingClientRect();
  const thumb = track.querySelector('.slide-to-confirm__thumb');
  trackLeft = rect.left;
  trackWidth.value = rect.width;
  thumbWidth.value = thumb ? thumb.offsetWidth : rect.height;
  maxX = Math.max(1, rect.width - thumbWidth.value - TRACK_INSET * 2);
}

const TRACK_INSET = 4; // matches $stc-inset in styles

function onPointerDown(event) {
  markPointerInteraction();
  if (props.disabled || props.loading || settled.value) return;
  measureTrack();
  // Grab only at (or near) the thumb. GRAB_SLOP widens the target well past
  // the visible circle so the control is easy to catch, while presses far
  // up the track stay inert.
  const offset = event.clientX - (trackLeft + TRACK_INSET + x.value);
  if (offset < -GRAB_SLOP_PX || offset > thumbWidth.value + GRAB_SLOP_PX) {
    return;
  }
  pointerId = event.pointerId;
  event.currentTarget.setPointerCapture(pointerId);
  // Keep the raw offset (even outside the thumb): the drag anchors exactly
  // at the press point, so position-at-press equals the current x — pure
  // 1:1 relative movement with no jump, no dead zone, and the full travel
  // distance still required from slop-zone grabs.
  grabOffset = offset;
  dragging.value = true;
  lastArmed = false;
  // New gesture — fresh attempt state.
  attempted = false;
}

// 'abandon' is emitted the moment a gesture shows real travel, NOT when it
// ends. The parent only holds it as a pending flag until the modal closes,
// and a 'confirm' clears it — so emitting early keeps the deferred
// semantics while surviving every way a gesture can die without a pointer
// event reaching us: release short of the threshold, pointer stolen by a
// scroll, a mid-drag disable reset (live odds tick), and the modal
// unmounting under the finger (parent beforeUnmount runs BEFORE this
// child's, so an emit at our own teardown would arrive after the parent
// already captured the flag).
function markAttempt() {
  if (attempted) return;
  attempted = true;
  emit('abandon');
}

function positionFromClientX(clientX) {
  const next = clientX - trackLeft - TRACK_INSET - grabOffset;
  return Math.min(Math.max(next, 0), maxX);
}

function applyPendingMove() {
  moveRaf = 0;
  if (pendingClientX === null || !dragging.value) return;
  x.value = positionFromClientX(pendingClientX);
  pendingClientX = null;
  if (x.value >= ATTEMPT_MIN_PX) markAttempt();
  if (armed.value !== lastArmed) {
    lastArmed = armed.value;
    // Haptic tick crossing the threshold (Android Chrome; no-op on iOS).
    if (armed.value && navigator.vibrate) navigator.vibrate(10);
  }
}

// Coalesce moves to one reactive write per frame — 120Hz pointers otherwise
// flood Vue with mid-frame updates and the thumb visibly jitters.
function onPointerMove(event) {
  if (!dragging.value || event.pointerId !== pointerId) return;
  pendingClientX = event.clientX;
  if (!moveRaf) moveRaf = requestAnimationFrame(applyPendingMove);
}

function cancelPendingMove() {
  if (moveRaf) cancelAnimationFrame(moveRaf);
  moveRaf = 0;
  pendingClientX = null;
}

function onPointerUp(event) {
  if (!dragging.value || event.pointerId !== pointerId) return;
  // Re-arm the click guard: the 1s window opened at pointerdown can expire
  // during a slow drag, and the browser's trailing click after release
  // would then look "synthetic" and confirm at any position.
  markPointerInteraction();
  cancelPendingMove();
  x.value = positionFromClientX(event.clientX);
  // Coalesced moves may never have flushed (quick flick) — the release
  // position is the last chance to record the attempt.
  if (x.value >= ATTEMPT_MIN_PX) markAttempt();
  dragging.value = false;
  pointerId = null;
  if (x.value / maxX >= CONFIRM_THRESHOLD) {
    confirm();
  } else {
    // Incomplete slide: short tick (distinct from the confirm pattern) so
    // the springback is felt as "nothing was placed". The abandon signal
    // itself was already emitted at the attempt crossing (markAttempt).
    if (x.value / maxX > ATTEMPT_THRESHOLD && navigator.vibrate) {
      navigator.vibrate(8);
    }
    x.value = 0;
  }
}

function onPointerCancel(event) {
  if (event.pointerId !== pointerId) return;
  markPointerInteraction();
  // A steal can land before the RAF flush ran — settle the last coalesced
  // position first so a >=ATTEMPT_MIN_PX travel still records its attempt
  // (mirrors the compensation in onPointerUp).
  if (pendingClientX !== null && dragging.value) {
    x.value = positionFromClientX(pendingClientX);
    if (x.value >= ATTEMPT_MIN_PX) markAttempt();
  }
  cancelPendingMove();
  dragging.value = false;
  pointerId = null;
  x.value = 0;
}

function confirm() {
  settled.value = true;
  x.value = maxX;
  if (navigator.vibrate) navigator.vibrate([15, 30, 15]);
  emit('confirm');
  clearTimeout(graceTimer);
  graceTimer = setTimeout(() => {
    if (!props.loading) reset();
  }, LOADING_GRACE_MS);
}

// Keyboard / assistive-tech path: a plain activation, no drag required.
// Held keys auto-repeat keydown; without the repeat guard a key still held
// through the post-confirm grace reset (e.g. the slippage-modal bail-out)
// would immediately re-fire confirm.
function handleKeyConfirm(event) {
  if (event?.repeat) return;
  if (props.disabled || props.loading || settled.value) return;
  measureTrack();
  confirm();
}

// Screen readers (VoiceOver/TalkBack double-tap) activate via a synthetic
// click that never delivers a pointerdown to the control. A physical tap
// always sends pointerdown first, and every pointerup is followed by a
// click — so a click preceded by our own pointer traffic must NOT confirm,
// or plain taps and sprung-back drags would place bets. detail === 0 is a
// second synthetic-activation signal (TalkBack, element.click()).
let sawPointerBeforeClick = false;
let pointerFlagTimer = null;
function markPointerInteraction() {
  sawPointerBeforeClick = true;
  clearTimeout(pointerFlagTimer);
  pointerFlagTimer = setTimeout(() => {
    sawPointerBeforeClick = false;
  }, 1000);
}
function handleClick(event) {
  const fromPhysicalPointer = sawPointerBeforeClick && event.detail > 0;
  sawPointerBeforeClick = false;
  if (fromPhysicalPointer) return; // real tap — the slide gesture is required
  handleKeyConfirm(event);
}

function reset() {
  clearTimeout(graceTimer);
  settled.value = false;
  dragging.value = false;
  x.value = 0;
}

watch(
  () => props.loading,
  (isLoading, wasLoading) => {
    if (isLoading) {
      clearTimeout(graceTimer);
      measureTrack();
      settled.value = true;
      x.value = maxX;
    } else if (wasLoading) {
      reset();
    }
  },
);

// If the parent disables mid-drag (odds moved, balance changed), bail out.
// The attempt (if real) was already reported at the crossing, so this reset
// can't lose it.
watch(
  () => props.disabled,
  (isDisabled) => {
    if (isDisabled && !settled.value) reset();
  },
);

onMounted(measureTrack);

onBeforeUnmount(() => {
  clearTimeout(graceTimer);
  clearTimeout(pointerFlagTimer);
  if (moveRaf) cancelAnimationFrame(moveRaf);
});
</script>

<style lang="scss" scoped>
@use '@/scss/main.scss';

// 68px matches the rendered height of the modal's Create New Offer button
// (two 0.925rem lines + 0.875rem vertical padding) so the two controls
// stack at equal thickness on mobile.
$stc-height: 68px;
$stc-inset: 4px;
$stc-radius: 35px; // matches $border-radius-button on the modal's buttons

.slide-to-confirm {
  position: relative;
  width: 100%;
  height: $stc-height;
  border-radius: $stc-radius;
  background-color: main.$color-primary_07;
  // Sunken groove: lit top edge falling into shadow, like a physical slot.
  background-image: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 38%,
    rgba(0, 0, 0, 0.18) 100%
  );
  box-shadow:
    inset 0 2px 8px rgba(0, 0, 0, 0.28),
    inset 0 -1px 0 rgba(255, 255, 255, 0.06),
    inset 0 0 0 1px rgba(255, 255, 255, 0.07);
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  // pan-y: vertical swipes still scroll the page; horizontal ones are ours.
  // (touch-action: none here would trap users trying to scroll past the
  // control.)
  touch-action: pan-y;
  transition: box-shadow 0.25s ease;

  &:focus-visible {
    box-shadow:
      inset 0 2px 8px rgba(0, 0, 0, 0.28),
      0 0 0 2px main.$blue-1101;
  }

  &--armed {
    box-shadow:
      inset 0 2px 8px rgba(0, 0, 0, 0.28),
      0 0 0 1.5px rgba(255, 255, 255, 0.35),
      0 0 22px rgba(123, 131, 216, 0.6);
  }

  // Matches the original .create-offer-btn: lighter track with dark text,
  // so the offer slide keeps the same background it always had.
  &--offer {
    background-color: main.$grey-1100_03;

    .slide-to-confirm__label {
      background-image:
        linear-gradient(
          100deg,
          rgba(255, 255, 255, 0) 42%,
          rgba(255, 255, 255, 0.9) 50%,
          rgba(255, 255, 255, 0) 58%
        ),
        linear-gradient(main.$blue-800_01, main.$blue-800_01);
    }

    .slide-to-confirm__armed-label {
      color: main.$blue-800_01;
      text-shadow: none;
    }

    .slide-to-confirm__thumb {
      // White background (like --max) so the thumb reads against the grey
      // offer track — $grey-1100_03 (track) and the base thumb's
      // $grey-1100_02 both resolve to #dfe1ef in light theme, blending it in.
      // Foreground stays the base $color-primary_07, which is dark on both
      // themes (#343b86 / #5A64A0) so the chevrons read on white either way.
      background-color: main.$white;
    }
  }

  &--max {
    background-color: main.$orange-100;

    &.slide-to-confirm--armed {
      box-shadow:
        inset 0 2px 8px rgba(0, 0, 0, 0.28),
        0 0 0 1.5px rgba(255, 255, 255, 0.4),
        0 0 22px rgba(248, 102, 36, 0.65);
    }
  }

  &--disabled {
    background-color: main.$grey-1800;
    background-image: none;
    box-shadow: none;

    .slide-to-confirm__label {
      background-image: none;
      color: main.$white;
      -webkit-text-fill-color: main.$white;
      animation: none;
    }

    .slide-to-confirm__thumb {
      opacity: 0.55;
    }

    .slide-to-confirm__chevrons {
      animation: none;
    }
  }
}

.slide-to-confirm__fill {
  position: absolute;
  inset: 0;
  // Scaled, not width-animated: transform stays on the compositor, width
  // would relayout every frame of the drag. The parent's overflow clip
  // shapes the edges, so no radius needed here.
  transform-origin: left center;
  will-change: transform;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04),
    rgba(255, 255, 255, 0.14) 65%,
    rgba(255, 255, 255, 0.26)
  );
  pointer-events: none;

  .slide-to-confirm--armed & {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.22) 65%,
      rgba(255, 255, 255, 0.38)
    );
  }

  // 1:1 with the finger while dragging; springs back with the thumb on
  // release.
  .slide-to-confirm--dragging & {
    transition: none;
  }

  .slide-to-confirm:not(.slide-to-confirm--dragging) & {
    transition: transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.15);
  }
}

.slide-to-confirm__label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.925rem;
  line-height: 1.35;
  padding: 0 ($stc-height - 6px) 0 ($stc-height + 4px);
  text-align: center;
  pointer-events: none;
  // Slide-to-unlock shimmer: a moving highlight band over a static base
  // layer. The base layer always covers the text, so the sweep can run off
  // the edges without letting glyphs go transparent.
  background-image:
    linear-gradient(
      100deg,
      rgba(255, 255, 255, 0) 42%,
      rgba(255, 255, 255, 0.95) 50%,
      rgba(255, 255, 255, 0) 58%
    ),
    linear-gradient(main.$grey-1100_02, main.$grey-1100_02);
  background-size:
    220% 100%,
    100% 100%;
  background-repeat: no-repeat;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: stc-shine 3s ease-in-out infinite;

  .slide-to-confirm--max & {
    background-image:
      linear-gradient(
        100deg,
        rgba(255, 255, 255, 0) 42%,
        #ffffff 50%,
        rgba(255, 255, 255, 0) 58%
      ),
      linear-gradient(rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.78));
  }
}

.slide-to-confirm__armed-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: main.$grey-1100_02;
  text-shadow: 0 0 12px rgba(255, 255, 255, 0.35);
  pointer-events: none;

  .slide-to-confirm--max & {
    color: main.$white;
  }
}

.slide-to-confirm__thumb {
  position: absolute;
  top: $stc-inset;
  left: $stc-inset;
  width: $stc-height - $stc-inset * 2;
  height: $stc-height - $stc-inset * 2;
  border-radius: 50%;
  background-color: main.$grey-1100_02;
  // Glassy dome: bright top highlight rolling off into the base color.
  background-image: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0) 60%
  );
  color: main.$color-primary_07;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  box-shadow:
    0 3px 10px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -2px 5px rgba(0, 0, 0, 0.14);
  will-change: transform;

  .slide-to-confirm--max & {
    background-color: main.$white;
    color: main.$orange-100;
  }

  .slide-to-confirm--dragging & {
    cursor: grabbing;
    box-shadow:
      0 5px 16px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      inset 0 -2px 5px rgba(0, 0, 0, 0.14);
  }

  .slide-to-confirm--armed & {
    animation: stc-armed-pulse 1.1s ease-in-out infinite;
  }

  // Direct 1:1 tracking while dragging; springy settle on release.
  .slide-to-confirm:not(.slide-to-confirm--dragging) & {
    transition: transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.15);
  }
}

@keyframes stc-shine {
  0%,
  18% {
    background-position:
      150% 0,
      0 0;
  }
  70%,
  100% {
    background-position:
      -50% 0,
      0 0;
  }
}

@keyframes stc-armed-pulse {
  0%,
  100% {
    box-shadow:
      0 0 0 3px rgba(255, 255, 255, 0.22),
      0 5px 16px rgba(0, 0, 0, 0.4);
  }
  50% {
    box-shadow:
      0 0 0 6px rgba(255, 255, 255, 0.12),
      0 5px 16px rgba(0, 0, 0, 0.4);
  }
}

.slide-to-confirm__chevrons {
  width: 20px;
  height: 16px;
}

.slide-to-confirm__chevron-1,
.slide-to-confirm__chevron-2 {
  opacity: 0.45;
}

// Traveling wave: each chevron brightens in sequence, pointing the way.
.slide-to-confirm:not(.slide-to-confirm--dragging):not(
    .slide-to-confirm--loading
  ):not(.slide-to-confirm--disabled) {
  .slide-to-confirm__chevrons {
    animation: stc-nudge 1.8s ease-in-out infinite;
  }

  .slide-to-confirm__chevron-1 {
    animation: stc-chevron-wave 1.8s ease-in-out infinite;
  }

  .slide-to-confirm__chevron-2 {
    animation: stc-chevron-wave 1.8s ease-in-out infinite 0.2s;
  }
}

// A firm grip deserves full-strength arrows.
.slide-to-confirm--dragging .slide-to-confirm__chevron-1,
.slide-to-confirm--dragging .slide-to-confirm__chevron-2 {
  opacity: 1;
}

@keyframes stc-nudge {
  0%,
  100% {
    transform: translateX(0);
  }
  15% {
    transform: translateX(2px);
  }
  30% {
    transform: translateX(0);
  }
}

@keyframes stc-chevron-wave {
  0%,
  40%,
  100% {
    opacity: 0.45;
  }
  15% {
    opacity: 1;
  }
}

.slide-to-confirm__spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2.5px solid currentColor;
  border-top-color: transparent;
  animation: stc-spin 0.7s linear infinite;
}

@keyframes stc-spin {
  to {
    transform: rotate(360deg);
  }
}

.stc-fade-enter-active,
.stc-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.stc-fade-enter-from,
.stc-fade-leave-to {
  opacity: 0;
  transform: scale(0.92);
}

@media (prefers-reduced-motion: reduce) {
  .slide-to-confirm__chevrons,
  .slide-to-confirm__chevron-1,
  .slide-to-confirm__chevron-2,
  .slide-to-confirm__label,
  .slide-to-confirm__thumb {
    animation: none !important;
  }
}
</style>
